import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import * as TestingActionsService from '../services/TestingActionsService';
import {
    ActionStatus,
    TestingAction,
} from '../types';
import {
    notifyError,
    notifySuccess,
} from '../../../components/PlatformNotification';
import {testingApiAvailable} from '../../testing-api-integration/config';

// Query keys
export const testingActionsKeys = {
    all: ['testing-actions'] as const,
    lists: () => [...testingActionsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...testingActionsKeys.lists(), {filters}] as const,
    details: () => [...testingActionsKeys.all, 'detail'] as const,
    detail: (id: string) => [...testingActionsKeys.details(), id] as const,
};

export const useTestingActionsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
                        queryKey: testingActionsKeys.list(filters),
                        queryFn: () => TestingActionsService.fetchRecords(filters),
                        enabled: testingApiAvailable,
                    });
};

export const useTestingActionQuery = (id: string) => {
    return useQuery({
                        queryKey: testingActionsKeys.detail(id),
                        queryFn: () => TestingActionsService.fetch(id),
                        enabled: testingApiAvailable && !!id,
                    });
};

export const useCreateTestingActionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: TestingActionsService.add,
                           onSuccess: () => {
                               notifySuccess('Action created');
                               queryClient.invalidateQueries({queryKey: testingActionsKeys.lists()});
                           },
                           onError: notifyError,
                       });
};

export const useUpdateTestingActionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: ({
                                            id,
                                            record
                                        }: { id: string; record: TestingAction }) =>
                               TestingActionsService.update(id, record),
                           onSuccess: (_, {id}) => {
                               notifySuccess('Action updated');
                               queryClient.invalidateQueries({queryKey: testingActionsKeys.lists()});
                               queryClient.invalidateQueries({queryKey: testingActionsKeys.detail(id)});
                           },
                           onError: notifyError,
                       });
};

export const useDeleteTestingActionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: TestingActionsService.deleteRecord,
                           onSuccess: () => {
                               notifySuccess('Action deleted');
                               queryClient.invalidateQueries({queryKey: testingActionsKeys.all});
                           },
                           onError: notifyError,
                       });
};

// Status transitions are a CONDITIONAL WRITE on the server. An illegal transition
// (e.g. RETIRED -> ACTIVE) fails, and that failure is surfaced to the user via
// notifyError rather than swallowed — the operator needs to see it.
export const useSetTestingActionStatusMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: ({
                                            id,
                                            targetStatus
                                        }: { id: string; targetStatus: ActionStatus }) =>
                               TestingActionsService.setStatus(id, targetStatus),
                           onSuccess: (record, {targetStatus}) => {
                               notifySuccess(`Action status set to ${targetStatus}`);
                               queryClient.invalidateQueries({queryKey: testingActionsKeys.lists()});
                               if (record?.id) {
                                   queryClient.invalidateQueries({queryKey: testingActionsKeys.detail(record.id)});
                               }
                           },
                           onError: notifyError,
                       });
};
